import type { Agreements } from '../agreement';
import { reconcileAgreements } from '../agreementSelection';
import type { SearchParameterAvailability } from '../searchParameterAvailability';
import {
  applyEditorialFamilyForEvents,
  type SearchParameters,
} from '../searchParameters';
import {
  scopeRememberedFor,
  type SearchPreferences,
} from '../searchPreferences';
import { offersSort, resolveSort, type SortProfile } from '../sortSelection';
import {
  applyPreferencesOnEntry,
  applyPreferencesOnScopeChange,
} from './applyPreferences';

/**
 * Everything a resolution reads that the search itself cannot say.
 *
 * Passed in rather than read, so the rule stays a function of its inputs and
 * the caller owns the I/O — the cookie, the profile request, the site config.
 */
export interface ResolutionContext {
  preferences: SearchPreferences;
  sortProfile: SortProfile;
  availability: SearchParameterAvailability;
}

/**
 * The search that actually runs, as opposed to the one the visitor stated.
 *
 * The two differ by what the visitor never said — remembered choices, values a
 * search implies, and the filters its context does not offer. Resolution
 * happens at exactly two moments, and they are the two methods here.
 *
 * The context is what both moments read and neither can derive: it is held
 * once. What varies by moment stays an argument — the entered search and the
 * agreements the visitor holds for an entry, the pair of searches for a
 * transition.
 */
export class ResolvedSearch {
  readonly #preferences: SearchPreferences;

  readonly #sortProfile: SortProfile;

  readonly #availability: SearchParameterAvailability;

  constructor({ preferences, sortProfile, availability }: ResolutionContext) {
    this.#preferences = preferences;
    this.#sortProfile = sortProfile;
    this.#availability = availability;
  }

  /**
   * The search a cold entry resolves to.
   *
   * The entered search states what the visitor asked for; this is the search
   * that runs. The two differ by what the visitor never said — remembered
   * choices, values a search implies, and the filters its context does not
   * offer.
   *
   * Entry is one of the two moments preferences are applied, the other being a
   * scope change. It is not a transition: there is no previous search to have
   * crossed out of, so the trigger is arriving rather than moving.
   *
   * The order is load-bearing:
   *
   * 1. **Preferences first.** They fill what the entry left unsaid, so every rung
   *    below sees the whole search rather than half of one.
   * 2. **Family before licenses and sort.** Both key off `family × assettype`;
   *    resolving either against a family about to change answers the wrong
   *    question.
   * 3. **Family before availability.** The two resolve the same contradiction in
   *    opposite directions — availability would drop an event from a
   *    creative-only family, where `applyEditorialFamilyForEvents` makes the family
   *    editorial and keeps the event. Running it first means the event survives.
   * 4. **Clearing last.** It is the only rung that removes rather than fills, so
   *    it judges the finished search.
   */
  onEntry(stated: SearchParameters, agreements: Agreements): SearchParameters {
    const remembered = applyPreferencesOnEntry(
      stated,
      this.#preferences,
      this.#availability
    );
    const editorial = applyEditorialFamilyForEvents(remembered);
    const licensed = this.#availability.removeUnavailableLicenses(editorial);
    const agreed = reconcileAgreements(licensed, agreements, this.#preferences);
    const sorted = resolveSort(agreed, this.#preferences, this.#sortProfile);

    return this.#availability.for(sorted).clearUnavailable();
  }

  // A stated sort the search no longer offers, replaced as a cold entry would
  // replace it. Returns the search itself when its sort is still offered.
  #withOfferedSort(searchParameters: SearchParameters): SearchParameters {
    if (!searchParameters.states('sort')) return searchParameters;
    if (offersSort(searchParameters, searchParameters.sort)) {
      return searchParameters;
    }

    return resolveSort(searchParameters, this.#preferences, this.#sortProfile);
  }

  /**
   * The search a scope change resolves to.
   *
   * One of the two moments preferences apply, the other being a cold entry.
   * Unlike entry this *is* a transition, so the trigger compares the two
   * searches: staying in scope applies nothing remembered and returns `next`
   * itself, so callers can use reference equality to skip work — unless `next`
   * states a sort it does not offer.
   *
   * That one exception is not a preference. Which sorts a search offers changes
   * within a scope too: archival offers no `best`, and only a portfolio takes
   * any sort in a creative image search. So switching an editorial search to
   * archival, or leaving a portfolio, can leave a sort the new search does not
   * offer. Left alone, the transition would keep it while a cold entry on the
   * same url would replace it, and a caller would have to resolve the sort
   * itself. The sort is resolved only when it is no longer offered, so a sort
   * the visitor chose and the new search still offers is never overridden.
   *
   * Resolving the sort is the point of running anything beyond the fill. A sort
   * is not a scope-bucket field — `editorialSort` and its siblings sit on the
   * preferences directly, so `applyPreferencesForScope` iterates past them and
   * only `resolveSort` can reach them. Without it a scope change leaves the sort
   * exactly as the previous scope left it: crossing into editorial ignores the
   * editorial sort the visitor chose last time, and crossing out of archival
   * carries `oldest` into a creative search that does not offer it. Nothing
   * downstream catches the second, because a sort's availability is `offersSort`
   * rather than SearchParameterAvailability.
   *
   * A sort the previous search only had because resolution filled it is dropped
   * before the sort is resolved. `resolveSort` always states a sort, so every
   * resolved search states one whether or not the visitor chose it; left in
   * place, a filled `best` from a creative search would outrank the editorial
   * sort the visitor remembers, and that remembered sort would never apply. A
   * sort the visitor did choose is kept, and still outranks the remembered one.
   *
   * The scope is compared here as well as inside the fill, which is not
   * redundancy: `applyPreferencesOnScopeChange` hands back `next` both when the
   * scope held and when it changed with nothing stored for the new one, so its
   * return cannot tell the two apart. Only the first should skip the sort.
   *
   * It narrows the licenses to the ones the new scope offers, after the fill
   * so a license restored for that scope is narrowed too. Clearing cannot do
   * this: it asks whether the license filter is offered at all, and mixed
   * image and mixed film both offer one, so `rf,rm` would cross into film
   * intact — a search a cold entry on the same url narrows to `rf`.
   *
   * It clears the parameters the new search does not offer, after the fill and
   * the sort, so a choice restored for the new scope is dropped again when that
   * scope cannot carry it. `alloweduse` does not survive creative into editorial.
   *
   * It does not derive a family from a leftover event. That derivation is right
   * on entry, where nobody has just asked for a family. Here the family change
   * is the transition, and reading a leftover event as a reason to make the
   * family editorial would undo the switch the visitor just made. The family
   * they chose wins, and the event is cleared with everything else the new
   * search does not offer.
   */
  onScopeChange(
    previous: SearchParameters,
    next: SearchParameters
  ): SearchParameters {
    if (scopeRememberedFor(next) === scopeRememberedFor(previous)) {
      return this.#withOfferedSort(next);
    }

    const remembered = applyPreferencesOnScopeChange(
      previous,
      next,
      this.#preferences,
      this.#availability
    );

    const licensed = this.#availability.removeUnavailableLicenses(remembered);
    const sortChoice = this.#isSortCarriedFromFill(previous, next)
      ? licensed.clear('sort')
      : licensed;

    return this.#availability
      .for(resolveSort(sortChoice, this.#preferences, this.#sortProfile))
      .clearUnavailable();
  }

  /**
   * Whether `next` carries the sort over from `previous` only because
   * resolution filled it there, rather than because the visitor chose it.
   *
   * A search does not record where a value came from, so this is an
   * inference: a carried sort is taken for a fill when it is exactly what
   * resolution would have filled for the previous search anyway. A sort the
   * transition itself sets is always a choice, and so is a carried one
   * resolution would not have produced. A visitor who chose the very sort the
   * fill would have given is indistinguishable from one who chose nothing,
   * and is treated the same.
   */
  #isSortCarriedFromFill(
    previous: SearchParameters,
    next: SearchParameters
  ): boolean {
    if (!next.states('sort') || !previous.states('sort')) return false;
    if (next.sort !== previous.sort) return false;

    const filled = resolveSort(
      previous.clear('sort'),
      this.#preferences,
      this.#sortProfile
    );

    return filled.sort === previous.sort;
  }
}
